import { cn } from "@/lib/utils";
import { Activity } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useConnectorHealth } from "@/hooks/useResearchHubTrust";
import { connectorSignal, humanise } from "./labels";

// A quiet, admin-only "data health" cue. Grey when all connectors are up to date, amber
// when something is behind — never a red klaxon. Read-only diagnostics only.
export function DataHealthBadge({ isAdmin }: { isAdmin: boolean }) {
  const { data, isLoading } = useConnectorHealth(isAdmin);
  if (!isAdmin) return null;

  const issues = (data ?? []).filter((r) => {
    const s = (r.signal ?? "").toLowerCase();
    return s && s !== "ok" && s !== "healthy";
  });
  const behind = issues.length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-body text-[11px] text-muted-foreground hover:bg-muted transition-colors"
        >
          <Activity className="h-3.5 w-3.5" aria-hidden />
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              behind ? "bg-[hsl(var(--financial-gold))]" : "bg-muted-foreground/40",
            )}
            aria-hidden
          />
          Data health
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 font-body text-sm">
        <div className="font-heading text-sm font-medium mb-2">Data health</div>
        {isLoading ? (
          <p className="text-muted-foreground text-xs">Checking…</p>
        ) : issues.length === 0 ? (
          <p className="text-muted-foreground text-xs">All connectors are up to date.</p>
        ) : (
          <ul className="space-y-2">
            {issues.map((r, i) => (
              <li key={i} className="text-xs">
                <div className="font-medium text-foreground">
                  {humanise(r.connector)} — {connectorSignal(r.signal)}
                </div>
                {r.detail && <div className="text-muted-foreground">{r.detail}</div>}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
